import { useState } from 'react'
import { AlertTriangle, CheckCircle, Clock, FileText, TrendingUp, Users, Shield, RefreshCw, Upload, Play, BarChart3, Calendar, Eye, Edit, Download, X, CheckCircle2, AlertCircle } from 'lucide-react'
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts'
import { useQuery } from '@tanstack/react-query'
import { dashboardService } from '../services/dashboard'

export default function DashboardPage() {
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'year'>('month')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showComplianceModal, setShowComplianceModal] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [showCalendarModal, setShowCalendarModal] = useState(false)
  const [notification, setNotification] = useState<{ type: 'success' | 'info' | 'error', message: string } | null>(null)

  // Fetch dashboard data from API
  const { data: dashboardData, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard', selectedPeriod],
    queryFn: () => dashboardService.getDashboard(selectedPeriod),
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  })

  // Extract data from response
  const metrics = dashboardData?.metrics
  const trendData = dashboardData?.trend_data || []
  const riskDistribution = dashboardData?.risk_distribution || []
  const projectStatus = dashboardData?.project_status || []

  const stats = [
    { 
      label: 'Active Projects', 
      value: metrics?.active_projects || 0, 
      icon: FileText, 
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      trend: null as string | null,
      trendUp: true
    },
    { 
      label: 'Pending Reviews', 
      value: metrics?.pending_reviews || 0, 
      icon: Clock, 
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      trend: null,
      trendUp: false
    },
    { 
      label: 'Compliance Score', 
      value: `${metrics?.compliance_score || 0}%`, 
      icon: CheckCircle, 
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      trend: null,
      trendUp: true
    },
    { 
      label: 'Risk Alerts', 
      value: metrics?.risk_alerts || 0, 
      icon: AlertTriangle, 
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      trend: null,
      trendUp: false
    },
  ]

  const recentActivity = dashboardData?.recent_activity || []
  const upcomingDeadlines = dashboardData?.upcoming_deadlines || []

  const getPriorityColor = (priority: string) => {
    switch(priority) {
      case 'high': return 'border-red-400'
      case 'medium': return 'border-yellow-400'
      case 'low': return 'border-blue-400'
      default: return 'border-gray-400'
    }
  }

  // Handle loading and error states
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-900 font-semibold">Failed to load dashboard</p>
          <button 
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 animate-fade-in-down`}>
          <div className={`flex items-center p-4 rounded-lg shadow-lg ${
            notification.type === 'success' ? 'bg-green-50 border border-green-200' :
            notification.type === 'info' ? 'bg-blue-50 border border-blue-200' :
            'bg-red-50 border border-red-200'
          }`}>
            <div className="flex-shrink-0">
              {notification.type === 'success' ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : notification.type === 'info' ? (
                <AlertCircle className="h-5 w-5 text-blue-600" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-red-600" />
              )}
            </div>
            <div className="ml-3 flex-1">
              <p className={`text-sm font-medium ${
                notification.type === 'success' ? 'text-green-800' :
                notification.type === 'info' ? 'text-blue-800' :
                'text-red-800'
              }`}>
                {notification.message}
              </p>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="ml-4 flex-shrink-0"
            >
              <X className={`h-4 w-4 ${
                notification.type === 'success' ? 'text-green-600 hover:text-green-800' :
                notification.type === 'info' ? 'text-blue-600 hover:text-blue-800' :
                'text-red-600 hover:text-red-800'
              }`} />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="mt-2 text-gray-600">Monitor your construction legal compliance in real-time</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => refetch()}
              className="p-2 text-gray-600 hover:text-primary-600 transition-colors"
              title="Refresh data"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
            <button
              onClick={() => setSelectedPeriod('week')}
              className={`px-4 py-2 rounded-lg ${selectedPeriod === 'week' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 border'}`}
            >
              Week
            </button>
            <button
              onClick={() => setSelectedPeriod('month')}
              className={`px-4 py-2 rounded-lg ${selectedPeriod === 'month' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 border'}`}
            >
              Month
            </button>
            <button
              onClick={() => setSelectedPeriod('year')}
              className={`px-4 py-2 rounded-lg ${selectedPeriod === 'year' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 border'}`}
            >
              Year
            </button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
                {stat.trend && (
                <div className={`flex items-center text-sm ${stat.trendUp ? 'text-green-600' : 'text-red-600'}`}>
                  <TrendingUp className={`h-4 w-4 mr-1 ${!stat.trendUp && 'rotate-180'}`} />
                  {stat.trend}
                </div>
                )}
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        {/* Compliance Trend Chart */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Compliance & Contract Trends</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="contracts" 
                stroke="#3b82f6" 
                strokeWidth={2}
                name="Contracts"
              />
              <Line 
                type="monotone" 
                dataKey="compliance" 
                stroke="#10b981" 
                strokeWidth={2}
                name="Compliance %"
              />
              <Line 
                type="monotone" 
                dataKey="risks" 
                stroke="#ef4444" 
                strokeWidth={2}
                name="Risk Alerts"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Risk Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Risk Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={riskDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {riskDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Project Status Bar Chart */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Status Overview</h2>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={projectStatus}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="status" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="count" fill="#3b82f6">
              {projectStatus.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Activity and Deadlines */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
              <button className="text-sm text-primary-600 hover:text-primary-700">View all</button>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3 group">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <div className="h-2 w-2 bg-primary-600 rounded-full"></div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 group-hover:text-primary-600 transition-colors cursor-pointer">
                      {activity.title}
                    </p>
                    {activity.type === 'contract' && (
                      <div className="mt-2 flex space-x-2">
                        <button
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                          onClick={() => setNotification({
                            type: 'info',
                            message: '👁️ Opening contract viewer...'
                          })}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View
                        </button>
                        <button
                          className="text-xs text-green-600 hover:text-green-800 flex items-center"
                          onClick={() => setNotification({
                            type: 'info',
                            message: '✏️ Opening contract editor...'
                          })}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </button>
                        <button
                          className="text-xs text-purple-600 hover:text-purple-800 flex items-center"
                          onClick={() => setNotification({
                            type: 'success',
                            message: '⬇️ Contract download started! Check your downloads folder.'
                          })}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </button>
                      </div>
                    )}
                    <div className="mt-1 flex items-center space-x-2 text-xs text-gray-500">
                      <Users className="h-3 w-3" />
                      <span>{activity.user}</span>
                      <span>•</span>
                      <span>{activity.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Upcoming Deadlines */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">Upcoming Deadlines</h2>
              <button
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
                onClick={() => setShowCalendarModal(true)}
              >
                <Calendar className="h-4 w-4 mr-1" />
                View calendar
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {upcomingDeadlines.map((deadline) => (
                <div key={deadline.id} className={`border-l-4 ${getPriorityColor(deadline.priority)} pl-4 hover:bg-gray-50 py-2 -my-2 rounded transition-colors`}>
                  <p className="text-sm font-medium text-gray-900">{deadline.title}</p>
                  <div className="mt-1 flex items-center space-x-2 text-xs text-gray-500">
                    <Shield className="h-3 w-3" />
                    <span>{deadline.project}</span>
                    <span>•</span>
                    <span className={deadline.priority === 'high' ? 'text-red-600 font-semibold' : ''}>
                      {deadline.due_in}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div className="text-white">
            <h3 className="text-lg font-semibold">Quick Actions</h3>
            <p className="text-primary-100 mt-1">Streamline your workflow with common tasks</p>
          </div>
          <div className="flex space-x-3">
            <button
              className="bg-white text-primary-600 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors flex items-center"
              onClick={() => setShowUploadModal(true)}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Contract
            </button>
            <button
              className="bg-white text-primary-600 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors flex items-center"
              onClick={() => setShowComplianceModal(true)}
            >
              <Play className="h-4 w-4 mr-2" />
              Run Compliance Check
            </button>
            <button
              className="bg-white text-primary-600 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors flex items-center"
              onClick={() => setShowReportModal(true)}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Generate Report
            </button>
          </div>
        </div>
      </div>

      {/* Upload Contract Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Upload Contract</h3>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              {/* Project Selection - MOST IMPORTANT */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <label className="block text-sm font-semibold text-blue-900 mb-2">
                  Select Project <span className="text-red-500">*</span>
                </label>
                <select className="mt-1 block w-full border border-blue-300 rounded-md px-3 py-2 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
                  <option value="">-- Select a Project --</option>
                  <option value="downtown-tower">Downtown Tower Development</option>
                  <option value="riverside-mall">Riverside Mall Renovation</option>
                  <option value="tech-campus">Tech Campus Construction</option>
                  <option value="bridge-project">Harbor Bridge Reconstruction</option>
                  <option value="hospital-wing">City Hospital New Wing</option>
                </select>
                <p className="mt-1 text-xs text-blue-700">The contract will be uploaded to the selected project</p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Contract File <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-semibold
                    file:bg-primary-50 file:text-primary-700
                    hover:file:bg-primary-100"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contract Type <span className="text-red-500">*</span>
                </label>
                <select className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2">
                  <option value="">-- Select Contract Type --</option>
                  <option>Main Contract</option>
                  <option>Amendment</option>
                  <option>Subcontract</option>
                  <option>Purchase Order</option>
                  <option>Change Order</option>
                  <option>Warranty</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contract Description (Optional)
                </label>
                <textarea
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  rows={3}
                  placeholder="Add any relevant notes about this contract..."
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowUploadModal(false)
                    setNotification({
                      type: 'success',
                      message: '📄 Contract uploaded successfully! The document has been added to the selected project and is now being processed.'
                    })
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                >
                  Upload to Project
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Check Modal */}
      {showComplianceModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Run Compliance Check</h3>
                <button
                  onClick={() => setShowComplianceModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Project
                </label>
                <select className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2">
                  <option>Downtown Tower Complex</option>
                  <option>Riverside Bridge Renovation</option>
                  <option>Shopping Mall Extension</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Check Type
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2" defaultChecked />
                    Safety Compliance
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2" defaultChecked />
                    Environmental Regulations
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2" />
                    Labor Law Compliance
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2" />
                    Building Code Compliance
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowComplianceModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowComplianceModal(false)
                    setNotification({
                      type: 'success',
                      message: '✅ Compliance check started successfully! Results will be available in 2-3 minutes. You\'ll receive a notification when complete.'
                    })
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                >
                  Start Check
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generate Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Generate Report</h3>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Report Type
                </label>
                <select className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2">
                  <option>Compliance Summary</option>
                  <option>Risk Analysis</option>
                  <option>Project Status</option>
                  <option>Financial Impact</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time Period
                </label>
                <select className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2">
                  <option>Last 30 days</option>
                  <option>Last 3 months</option>
                  <option>Last 6 months</option>
                  <option>Last year</option>
                  <option>Custom range</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Format
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input type="radio" name="format" className="mr-2" defaultChecked />
                    PDF
                  </label>
                  <label className="flex items-center">
                    <input type="radio" name="format" className="mr-2" />
                    Excel
                  </label>
                  <label className="flex items-center">
                    <input type="radio" name="format" className="mr-2" />
                    CSV
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowReportModal(false)
                    setNotification({
                      type: 'info',
                      message: '📊 Report generation in progress! You will receive an email when your report is ready for download.'
                    })
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                >
                  Generate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Modal */}
      {showCalendarModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Project Calendar</h3>
                <button
                  onClick={() => setShowCalendarModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              <div className="calendar-view">
                <div className="mb-4 text-center">
                  <h4 className="text-lg font-semibold text-gray-900">
                    {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </h4>
                </div>
                <div className="grid grid-cols-7 gap-1 mb-4">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <div key={day} className="p-2 text-center font-semibold text-gray-700 bg-gray-100">
                      {day}
                    </div>
                  ))}
                  {Array.from({ length: 35 }, (_, i) => {
                    const day = i - 3; // Start from a few days before month
                    const isCurrentMonth = day > 0 && day <= 31;
                    const hasEvent = [5, 12, 18, 25].includes(day);
                    return (
                      <div
                        key={i}
                        className={`p-2 h-16 border ${isCurrentMonth ? 'bg-white' : 'bg-gray-50'} ${hasEvent ? 'bg-blue-50' : ''}`}
                      >
                        {isCurrentMonth && (
                          <div>
                            <span className="text-sm">{day}</span>
                            {hasEvent && (
                              <div className="text-xs text-blue-600 mt-1">
                                {day === 5 && 'Contract Review'}
                                {day === 12 && 'Safety Inspection'}
                                {day === 18 && 'Compliance Audit'}
                                {day === 25 && 'Project Deadline'}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Upcoming Events</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-blue-50 rounded">
                      <span className="text-sm">Contract Review - Downtown Tower</span>
                      <span className="text-xs text-gray-600">
                        {new Date(new Date().getFullYear(), new Date().getMonth(), 5).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-yellow-50 rounded">
                      <span className="text-sm">Safety Inspection - Bridge Project</span>
                      <span className="text-xs text-gray-600">
                        {new Date(new Date().getFullYear(), new Date().getMonth(), 12).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                      <span className="text-sm">Compliance Audit - Mall Extension</span>
                      <span className="text-xs text-gray-600">
                        {new Date(new Date().getFullYear(), new Date().getMonth(), 18).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowCalendarModal(false)}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}